// Modo "Mostrar cómo funciona" — pantalla propia (ruta /mostrar-como-funciona).
//
// Video corto (5-8 s) de un mueble en uso: una persona se sienta, la cámara recorre el
// ambiente, o el mueble se transforma (sofá cama que se abre, mesa que se extiende).
// Sólo para marcas del rubro Hogar y Decoración — `soloHogarDeco` en `modesCatalog`
// esconde la card, y esta pantalla además rebota a la home si alguien llega por URL.
// Ver ADR-2026-09-03-modo-mostrar-como-funciona.
//
// Toma la anatomía del modo Modelo v2 (ADR-2026-08-21-modelo-v2-una-sola-columna):
//   • una COLUMNA de configuración a la izquierda, con las cards de 76px, el pie fijo
//     con los ajustes y el Generar, y el ancho elástico `ANCHO_PANEL_CONFIG`;
//   • lo que no se resuelve en la columna se elige en el PANEL LATERAL, que se abre
//     sobre el área de la derecha (productos, ambiente, formato);
//   • a la derecha, la muestra del modo hasta que exista una tanda, y las tandas
//     después.
//
// Dos pasos sobre la misma pantalla: (1) elegir el producto, (2) configurar el video.
// La columna es la misma en los dos —avanzar agrega lo que falta debajo de la card de
// productos y cambia el CTA—, que es lo que hace que se lea como un avance y no como otra
// pantalla (el mismo argumento que ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
//
// SIN BACKEND todavía: el Generar dibuja la tanda "Generando…" en la columna de la
// derecha y deja el payload en consola. Es la primera vuelta del modo, acordada así:
// primero validar el flujo, después conectarlo.
//
// EN MOBILE son los MISMOS dos pasos en dos pantallas full-screen, con la anatomía de
// Modelo y de "Ambiente completo" (ADR-2026-09-10-ambiente-completo-en-el-telefono):
// (1) la grilla de creaciones con su CTA al pie, (2) "Configurar el video" con las mismas
// cards de la columna y el MISMO pie de desktop abajo. Lo que en desktop se elige en el
// panel lateral, acá se elige en una CAPA full-screen con el mismo contenido —Formato es
// la excepción, que tiene su bottom sheet de siempre—, y la card de la imagen de partida
// no abre panel: devuelve al paso 1, que es donde vive la grilla.
// Ver ADR-2026-09-10-los-modos-de-deco-en-el-telefono.
//
// OJO con los colores: los tokens `brand-*` no declaran `<alpha-value>`, así que
// `text-brand-black/90` no emite regla (memoria del repo). Acá se usa `text-black/<n>`
// para los grises y `text-brand-black` pelado para el negro.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LucideIcon,
  Armchair,
  ArrowLeftRight,
  ArrowRight,
  BedDouble,
  BookOpen,
  ChevronRight,
  Clapperboard,
  ImageUp,
  PencilLine,
  Sofa,
  Sparkles,
  Sun,
  ImagePlus,
  Upload,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import { useShellEscritorio } from '../../../hooks/useShellEscritorio';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { esRubroHogarDeco } from '../../../lib/rubro';
import CachedImg from '../../../components/CachedImg';
import { EncabezadoPantalla } from '../../../components/PantallaChrome';
import { DEFAULT_FORMAT_ID, getFormatLabel } from '../mockData';
import { GeneradasPickGrid, type GeneradaPick } from './shared/GeneradasPickGrid';
import { CardProductos } from '../modelo/PanelCompacto';
import { fetchUserProducts } from '../productsApi';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { CapaModoMobile } from './shared/CapaModoMobile';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { FormatPicker, FormatPickerGrid } from '../FormatPicker';
import { FillBarSliderV2 } from '../components/FillBarSliderV2';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  CeldaAjuste,
  ValorConChevron,
} from '../components/AjustesRapidosProducto';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
import { ANCHO_PANEL_CONFIG, FILA_MODO_REDISENO } from '../components/layoutModo';
import { nombreCortoModo } from '../modesCatalog';
import {
  ACCIONES_VIDEO,
  AMBIENTES,
  AMBIENTE_PROPIO,
  AMBIENTE_PROPIO_ID,
  DURACION_DEFAULT,
  DURACION_MAX,
  DURACION_MIN,
  accionVideoPorId,
  ambientePorId,
  costoVideo,
  ratioDeFormato,
  type AccionVideoId,
  type AmbienteElegido,
  type AmbienteId,
  type ExtremoTransformacion,
} from '../mostrarComoFunciona';

/** Segmento de ruta del modo — el mismo que `path` en `modesCatalog`. */
const PATH_MODO = 'mostrar-como-funciona';

// El ancho del panel y la sangría de la fila salen de `layoutModo`, que es donde viven
// para los modos del rediseño. Este modo tenía su propia copia y se había quedado en el
// valor VIEJO —660px contra los 600 de hoy
// (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos)—, así que abría con un panel más
// gordo que Automática (ADR-2026-09-24-variantes-usa-la-caja-de-los-otros-modos).

/** Ancho del panel lateral — el de los pickers de Modelo (`ANCHO_PANEL_LATERAL`). */
const ANCHO_PANEL_LATERAL = 615;

/**
 * Aire entre la columna de configuración y el panel lateral, en px.
 *
 * El mismo que en "Variantes de acabado"
 * (ADR-2026-09-04-el-panel-lateral-se-separa-de-la-columna): pegado al borde de la
 * columna, las dos cajas blancas se leían como una sola y el panel parecía montado
 * ENCIMA de la columna en vez de al lado.
 */
const SEPARACION_PANEL = 24;

/** Columnas de las grillas del panel lateral, como en Modelo. */
const COLUMNAS_PANEL_LATERAL = 3;

/**
 * Portada del modo mientras no hay tanda. PLACEHOLDER: es el ropero en contexto que ya
 * usa el Catálogo del rubro; falta un cuadro propio de un mueble en uso.
 */
const PORTADA = '/images/modo_catalogo_muebles/hero_contexto.jpg';

/**
 * Lo que dice la MUESTRA del modo — qué hace, en una línea y un ejemplo.
 *
 * Vive acá arriba porque se dibuja en DOS lugares: la columna de la derecha en desktop y,
 * en el teléfono, arriba del paso 1 (ADR-2026-09-10-la-muestra-del-modo-tambien-en-mobile).
 */
/**
 * Cuántas miniaturas lleva la card de la pantalla de arranque, y con qué caer si el
 * catálogo no tiene ninguna. Son un EJEMPLO de lo que hay del otro lado —van atenuadas y
 * sin tilde—, no una selección.
 */
const MINIATURAS_CARD = 3;
const PLACEHOLDERS_MUEBLES = [
  '/images/vistas_muebles/packshot.png',
  '/images/modo_catalogo_muebles/hero_packshot.png',
];

/**
 * Cuánto dura el overlay de generación en el teléfono.
 *
 * MOCK: sin backend no hay nada que esperar, así que la barra se paceo sola y al llegar a
 * 100% pasa a la pantalla de resultados (`completeAtEnd`). Cuando entre la llamada de
 * verdad, esto pasa a ser el tiempo ESTIMADO —un video tarda minutos, no segundos— y
 * quien cierra el overlay es `completeNow` con la respuesta.
 */
const CARGA_MOCK_MS = 6000;

const TITULO_MUESTRA = 'Elegí tu foto y contá qué pasa';
const BAJADA_MUESTRA = 'Atlas arma un video corto del mueble en uso';

/**
 * De dónde sale la foto del mueble que va a aparecer en el video.
 *
 * NO es un producto del catálogo: el video parte de una FOTO ya lograda —una creación del
 * usuario o un archivo suyo— y la pone en movimiento. El packshot crudo del catálogo es
 * la foto que se subió para poder generar, no la que se quiere animar. Es la misma
 * decisión que tomó "Variantes de acabado"
 * (ADR-2026-09-04-mostrar-como-funciona-parte-de-una-creacion).
 */
interface ImagenOrigen {
  /** Clave de la selección: el `id` de la creación, o una propia si es un archivo. */
  key: string;
  tipo: 'generada' | 'subida';
  /** Para dibujarla: signed URL de la creación, u `objectURL` del archivo. */
  url: string;
  /** Id de la fila de `imagenes_generadas` — sólo en `generada`, y es lo que viaja. */
  id?: string;
  /** El archivo — sólo en `subida`, y es lo que viajaría. */
  archivo?: File;
  nombre: string;
  /**
   * De qué producto es la creación. Lo usa "Se transforma": la foto de cómo TERMINA
   * tiene que ser del mismo mueble, así que su panel se acota a este producto
   * (ADR-2026-09-04-como-termina-se-elige-de-las-creaciones-del-producto).
   */
  varianteId?: string | null;
  producto?: string | null;
}

/**
 * Los pasos del modo.
 *
 * `presentacion` y `resultados` son SÓLO de mobile, con la misma anatomía que en
 * "Variantes de acabado" y "Ambiente completo": el modo abre con su muestra y una card
 * para traer la foto, y lo que sale de Generar vive en una pantalla propia detrás del
 * overlay. En desktop ninguno de los dos existe — allá la muestra y las tandas viven en
 * la columna de la derecha, al lado de la configuración y no encima.
 */
type Paso = 'presentacion' | 'imagen' | 'configurar' | 'resultados';

/**
 * Cómo se resuelve el FINAL de la transformación
 * (ADR-2026-09-04-como-termina-tiene-tres-maneras).
 *
 * Antes sólo existía `foto`: había que tener la foto del mueble abierto, y si no, el modo
 * no se podía usar. Las otras dos salidas son las que hacen que se pueda: la IA lo
 * interpreta sola, o el usuario lo cuenta con palabras.
 */
type FinalModo = 'ia' | 'descrito' | 'foto';

interface OpcionFinal {
  id: FinalModo;
  nombre: string;
  descripcion: string;
}

const OPCIONES_FINAL: OpcionFinal[] = [
  {
    id: 'ia',
    nombre: 'Que lo resuelva Atlas',
    descripcion: 'La IA interpreta cómo se abre o se despliega este mueble.',
  },
  {
    id: 'descrito',
    nombre: 'Lo describo',
    descripcion: 'Contá en qué termina: abierto, extendido, desplegado.',
  },
  {
    id: 'foto',
    nombre: 'Uso una foto',
    descripcion: 'Una creación de este producto, o una foto tuya.',
  },
];
type PanelLateral = 'origen' | 'ambiente' | 'formato' | 'final' | null;

/** Una tanda: lo que salió de un toque de Generar. Sin backend, sólo su forma. */
interface TandaVideo {
  id: string;
  duracion: number;
  ratio: number;
  accion: AccionVideoId;
  creadaEn: number;
}

/** Ícono de cada manera de resolver el final. */
const ICONO_FINAL: Record<FinalModo, LucideIcon> = {
  ia: Sparkles,
  descrito: PencilLine,
  foto: ImagePlus,
};

/** Ícono de cada acción, en el mismo trazo de `lucide` que el resto de la columna. */
const ICONO_ACCION: Record<AccionVideoId, LucideIcon> = {
  usa: Armchair,
  recorre: Clapperboard,
  transforma: ArrowLeftRight,
};

/** Ícono de cada ambiente, hasta que lleguen las fotos (ver `AMBIENTES`). */
const ICONO_AMBIENTE: Record<AmbienteId, LucideIcon> = {
  living: Sofa,
  comedor: UtensilsCrossed,
  dormitorio: BedDouble,
  balcon: Sun,
  estudio: BookOpen,
};

/**
 * Card en FILA de 76px —miniatura cuadrada, etiqueta y valor, chevron— la misma forma que
 * las cards de Modelo, Pose y Fondo de la columna de Modelo v2
 * (ADR-2026-08-31-modelo-v2-cards-en-fila). Local y no importada: la de `PanelCompacto`
 * no se exporta y trae la lógica de mosaico de modelos que acá no aplica.
 */
const CardFila: React.FC<{
  etiqueta: string;
  valor: string;
  miniatura: React.ReactNode;
  onClick: () => void;
  /** Resaltada: es la card cuyo panel está abierto. */
  activa?: boolean;
}> = ({ etiqueta, valor, miniatura, onClick, activa = false }) => (
  <button
    type="button"
    data-abre-panel-lateral
    onClick={onClick}
    className={`w-full h-[76px] shrink-0 rounded-2xl p-3 flex items-center gap-3 text-left transition-colors ${
      activa
        ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)]'
        : 'bg-black/[0.035] hover:bg-black/[0.06]'
    }`}
  >
    <div className="relative h-full aspect-square shrink-0 rounded-xl overflow-hidden bg-white grid place-items-center">
      {miniatura}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-bold text-brand-black leading-tight">{etiqueta}</p>
      <p className="text-[13px] text-black/50 leading-tight mt-0.5 truncate">{valor}</p>
    </div>
    <ChevronRight
      className={`w-[18px] h-[18px] shrink-0 ${activa ? 'text-brand-primary' : 'text-black/30'}`}
    />
  </button>
);

/**
 * Recuadro "generando" de la tanda: el mismo de `PanelTandaCreaciones` (que no lo
 * exporta), con el fondo animado con el que la app ya dice "esto lo está haciendo la IA".
 */
const RecuadroGenerando: React.FC<{ ratio: number; duracion: number }> = ({
  ratio,
  duracion,
}) => (
  <div
    className="relative overflow-hidden rounded-2xl bg-brand-primary"
    style={{ aspectRatio: String(ratio) }}
  >
    <AnimatedBlurBg selected />
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-4 text-white">
      <Sparkles className="w-8 h-8 drop-shadow" strokeWidth={1.5} />
      <span className="text-[13px] font-semibold drop-shadow">
        Generando video de {duracion} s…
      </span>
      <span className="relative h-1 w-2/3 overflow-hidden rounded-full bg-white/30">
        <span className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-white animate-tanda-barra" />
      </span>
    </div>
  </div>
);

/** Slot de "El antes y el después": una foto cargada, o la zona para subirla. */
const SlotExtremo: React.FC<{
  etiqueta: string;
  extremo: ExtremoTransformacion | null;
  onElegir: () => void;
  onQuitar: () => void;
  /**
   * Marca el disparador con `data-abre-panel-lateral`, que es lo que `PanelLateralModo`
   * mira para no cerrarse con el mismo click que lo abre.
   */
  abrePanelLateral?: boolean;
  /**
   * Lo decidido cuando NO es una foto: la IA resolviéndolo, o el texto que lo describe
   * (ADR-2026-09-04-como-termina-tiene-tres-maneras). Ocupa el mismo recuadro que la foto
   * —el slot tiene que mostrar la decisión, sea cual sea— y se toca para cambiarla.
   */
  resumen?: { icono: LucideIcon; titulo: string } | null;
  /** Qué dice el botón vacío. Con tres maneras, "Subir foto" ya no las nombra. */
  textoVacio?: string;
  /** Y la línea de abajo, que explica qué se espera de este extremo. */
  pie?: string;
}> = ({
  etiqueta,
  extremo,
  onElegir,
  onQuitar,
  abrePanelLateral = false,
  resumen = null,
  textoVacio,
  pie,
}) => (
  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
    <span className="text-[11.5px] font-semibold text-black/70">{etiqueta}</span>
    {resumen ? (
      <button
        type="button"
        onClick={onElegir}
        {...(abrePanelLateral ? { 'data-abre-panel-lateral': true } : {})}
        className="aspect-video rounded-[10px] bg-white ring-1 ring-black/[0.08] hover:ring-brand-primary/45 flex flex-col items-center justify-center gap-1.5 px-3 text-center transition-shadow"
      >
        <span className="w-8 h-8 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] grid place-items-center text-brand-primary">
          <resumen.icono className="w-4 h-4" />
        </span>
        <span className="text-[12px] font-semibold text-brand-black leading-tight line-clamp-2">
          {resumen.titulo}
        </span>
      </button>
    ) : extremo ? (
      <div className="relative aspect-video rounded-[10px] overflow-hidden bg-white ring-1 ring-black/[0.08]">
        <CachedImg
          src={extremo.url}
          alt={etiqueta}
          className="absolute inset-0 w-full h-full object-contain p-1.5"
        />
        {/* La X REEMPLAZA, no borra sin más: abre el selector de archivo con la foto
            todavía puesta, así cambiarla es un gesto y no dos. Salvo la del producto,
            que se puede quitar para subir otra. */}
        <button
          type="button"
          onClick={onQuitar}
          {...(abrePanelLateral ? { 'data-abre-panel-lateral': true } : {})}
          aria-label={`Quitar ${etiqueta.toLowerCase()}`}
          className="absolute top-1.5 right-1.5 w-[22px] h-[22px] rounded-full bg-white/95 text-black/55 grid place-items-center shadow-sm hover:text-brand-black transition-colors"
        >
          <X className="w-3 h-3" strokeWidth={2.6} />
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={onElegir}
        {...(abrePanelLateral ? { 'data-abre-panel-lateral': true } : {})}
        className="aspect-video rounded-[10px] bg-white border-[1.5px] border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.35)] hover:border-[rgb(var(--brand-primary-rgb)_/_0.6)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.03)] flex flex-col items-center justify-center gap-1.5 transition-colors"
      >
        <span className="w-8 h-8 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] grid place-items-center text-brand-primary">
          {abrePanelLateral ? (
            <ImagePlus className="w-4 h-4" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </span>
        <span className="text-[12px] font-semibold text-brand-primary">
          {textoVacio ?? (abrePanelLateral ? 'Elegir cómo termina' : 'Subir foto')}
        </span>
      </button>
    )}
    <span className="text-[11.5px] text-black/50 truncate">
      {extremo ? extremo.nombre : (pie ?? 'Mismo mueble, mismo encuadre')}
    </span>
  </div>
);

export default function MostrarComoFuncionaMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  // Qué pantalla se dibuja. Se decide en JS y no sólo con `md:` porque las dos ramas no
  // son la misma pantalla con otro CSS: montarlas a la vez levantaría DOS grillas de
  // creaciones —cada una con sus consultas y sus URLs firmadas— y las capas de mobile se
  // portalean al `body`, fuera del alcance de cualquier `md:hidden` que les pusiéramos
  // encima. Es el mismo corte de 768px con el que el shell elige sidebar o barra
  // inferior, así que la pantalla del modo y su chrome cambian juntos.
  const esEscritorio = useShellEscritorio();
  const { rubro, loaded: rubroCargado } = useRubroMarcaActiva();

  // ── Gate de rubro ─────────────────────────────────────────────────────────
  // La card ya no se muestra fuera de Hogar y Decoración (`soloHogarDeco`), pero la ruta
  // existe igual: alguien con el link, o una cuenta que cambió de marca con la pantalla
  // abierta. Con el rubro resuelto y sin ser el correcto, a la home.
  useEffect(() => {
    if (rubroCargado && !esRubroHogarDeco(rubro)) {
      navigate('/interfaz-v2', { replace: true });
    }
  }, [rubroCargado, rubro, navigate]);

  // ── Cuenta ────────────────────────────────────────────────────────────────
  // La empresa es lo que necesita la grilla de creaciones. La marca activa y el catálogo
  // se cargan SÓLO para la cara de la card de arranque: lo que se elige en este modo
  // sigue siendo una imagen, no un producto
  // (ADR-2026-09-04-mostrar-como-funciona-parte-de-una-creacion).
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [miniaturasCatalogo, setMiniaturasCatalogo] = useState<string[]>([]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: perfil } = await supabase
        .from('profiles')
        .select('id_company, current_brand_id')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const p = perfil as {
        id_company?: string | null;
        current_brand_id?: string | null;
      } | null;
      setCompanyId(p?.id_company ?? null);
      setBrandId(p?.current_brand_id ?? null);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  /**
   * Las últimas fotos del catálogo, para la cara de la card de arranque.
   *
   * `fetchUserProducts` devuelve el catálogo ordenado por `created_at` descendente, así
   * que las primeras SON las últimas cargadas; se filtran las que no tienen imagen
   * firmada —un hueco blanco en el abanico se lee como una foto rota—. Es el mismo
   * criterio que la card vacía de "Ambiente completo"
   * (ADR-2026-09-10-la-card-vacia-muestra-los-ultimos-productos).
   *
   * Acotado a la MARCA activa, que es lo que las deja dentro del rubro: con el catálogo
   * entero, una empresa con marcas de varios rubros abría un modo de muebles con remeras
   * en la card.
   */
  useEffect(() => {
    if (!companyId) return;
    let cancelado = false;
    (async () => {
      try {
        const lista = await fetchUserProducts(companyId, brandId);
        if (cancelado) return;
        setMiniaturasCatalogo(
          lista
            .map((p) => p.imageUrl)
            .filter((u): u is string => !!u)
            .slice(0, MINIATURAS_CARD),
        );
      } catch (e) {
        console.error('[MostrarComoFuncionaMode] catálogo para las miniaturas', e);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId]);

  // ── Lo que se elige ───────────────────────────────────────────────────────
  // Arranca en la presentación SÓLO en el teléfono: en desktop ese paso no existe (ver
  // `Paso`). `useShellEscritorio` resuelve sincrónicamente en el primer render, y cruzar
  // los 768px remonta el árbol entero, que es cuando este valor se vuelve a elegir.
  const [paso, setPaso] = useState<Paso>(esEscritorio ? 'imagen' : 'presentacion');
  // El overlay de generación, SÓLO en el teléfono: en desktop la tanda aparece en la
  // columna de la derecha, que se ve entera mientras se genera.
  const [generandoMobile, setGenerandoMobile] = useState(false);
  const [origen, setOrigen] = useState<ImagenOrigen | null>(null);
  const [accion, setAccion] = useState<AccionVideoId>('usa');
  const [ambienteId, setAmbienteId] = useState<AmbienteElegido>('living');
  /**
   * El ambiente escrito a mano, cuando está elegido "Lo describo yo". Se guarda aparte de
   * `ambienteId` para que volver a un ambiente curado y arrepentirse no lo borre.
   */
  const [ambientePropio, setAmbientePropio] = useState('');
  const [duracion, setDuracion] = useState(DURACION_DEFAULT);
  const [formatoId, setFormatoId] = useState<string>(DEFAULT_FORMAT_ID);
  const [instruccion, setInstruccion] = useState('');
  const [antes, setAntes] = useState<ExtremoTransformacion | null>(null);
  const [despues, setDespues] = useState<ExtremoTransformacion | null>(null);
  // Arranca en `ia`: es la que deja usar el modo sin tener nada más que la foto de
  // partida, y las otras dos están a un toque en el mismo panel.
  const [finalModo, setFinalModo] = useState<FinalModo>('ia');
  const [finalDescrito, setFinalDescrito] = useState('');
  const [tandas, setTandas] = useState<TandaVideo[]>([]);
  const [panelLateral, setPanelLateral] = useState<PanelLateral>(null);

  // "Cómo empieza" arranca con la foto de partida: es el estado inicial más probable y
  // ahorra subirla. Se puede quitar y poner otra.
  useEffect(() => {
    if (accion !== 'transforma' || !origen || antes) return;
    setAntes({
      url: origen.url,
      nombre: origen.tipo === 'generada' ? 'Tu foto de partida' : origen.nombre,
      esDeLaFoto: true,
    });
  }, [accion, origen, antes]);

  // Cambiar de foto invalida un "antes" que era la anterior.
  useEffect(() => {
    setAntes((a) => (a?.esDeLaFoto ? null : a));
  }, [origen?.key]);

  // ── Los `objectURL` de las fotos subidas ──────────────────────────────────
  // Se liberan al REEMPLAZARLAS y al salir. Sólo al salir no alcanza: probar fotos es
  // justamente lo que se hace en este modo —se sube una, no convence, se sube otra— y
  // cada `createObjectURL` deja vivo el blob entero hasta que se lo revoca. Con fotos de
  // teléfono (varios MB cada una) una sesión de pruebas se llevaba puesta toda la
  // memoria sin que nada de eso estuviera en pantalla.
  //
  // Quién puede estar usando una url, que es lo que decide si se puede soltar:
  //   • `origen`, `antes` y `despues` — lo que se ve. Ojo: "Cómo empieza" puede ser LA
  //     MISMA url que la foto de partida (`esDeLaFoto`), así que ahí no es de quien la
  //     reemplaza soltarla.
  //   • las tandas ya generadas — NO: `TandaVideo` no guarda urls (a diferencia de
  //     "Variantes de acabado", donde el resultado sí congela la foto de partida y por
  //     eso allá no se puede soltar nada antes de salir).
  const urlsCreadas = useRef<string[]>([]);
  /** Suelta una url subida. Ignora las que no creamos acá (las firmadas de Supabase). */
  const liberarUrl = (url: string | undefined | null) => {
    if (!url || !urlsCreadas.current.includes(url)) return;
    URL.revokeObjectURL(url);
    urlsCreadas.current = urlsCreadas.current.filter((u) => u !== url);
  };
  useEffect(
    () => () => {
      urlsCreadas.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  const inputAntesRef = useRef<HTMLInputElement>(null);
  const inputDespuesRef = useRef<HTMLInputElement>(null);
  const inputImagenRef = useRef<HTMLInputElement>(null);

  /**
   * Elegir la foto de partida. Las dos vías dejan lo mismo en `origen`, y las dos cierran
   * el panel: acá la selección es ÚNICA —un video, un mueble—, así que elegir termina.
   *
   * El archivo se queda LOCAL (`objectURL`) en vez de subirse a un bucket: el modo
   * todavía no genera, así que subir dejaría archivos huérfanos en storage por cada
   * prueba. Con backend, este handler pasa a ser `subirImagenParaAnimar`.
   */
  const elegirGenerada = (pick: GeneradaPick) => {
    // Pasar de una foto subida a una creación también reemplaza: mismo cuidado que en
    // `onArchivoImagen`.
    if (antes?.esDeLaFoto) setAntes(null);
    liberarUrl(origen?.tipo === 'subida' ? origen.url : null);
    setOrigen({
      key: pick.id,
      tipo: 'generada',
      id: pick.id,
      url: pick.signedUrl,
      nombre: 'Creación tuya',
      varianteId: pick.varianteId,
      producto: pick.producto,
    });
    setPanelLateral(null);
  };

  /**
   * "Cómo termina", elegida de las creaciones del MISMO producto. Subir sigue estando —el
   * mueble abierto o desplegado suele ser una foto propia que todavía no se generó—, pero
   * ya no es la única salida: si la vertiente abierta ya se generó, está a un toque.
   */
  const elegirFinalGenerada = (pick: GeneradaPick) => {
    // Elegir una creación reemplaza un "cómo termina" que podía ser una foto subida.
    if (despues && !despues.esDeLaFoto) liberarUrl(despues.url);
    setDespues({
      url: pick.signedUrl,
      nombre: pick.producto ?? 'Creación tuya',
      esDeLaFoto: false,
    });
    setPanelLateral(null);
  };

  const onArchivoImagen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    urlsCreadas.current.push(url);
    // La foto que se va suelta su blob. "Cómo empieza", cuando era ESTA foto, se va con
    // ella —lo hace el efecto de `origen.key`, y se repite acá para que las dos cosas
    // entren en el mismo render: si no, quedaba un paint con la url ya revocada puesta
    // en un `<img>`, o sea la foto rota justo al cambiarla.
    if (antes?.esDeLaFoto) setAntes(null);
    liberarUrl(origen?.tipo === 'subida' ? origen.url : null);
    setOrigen({
      key: `subida-${Date.now()}`,
      tipo: 'subida',
      url,
      archivo,
      nombre: archivo.name,
    });
    setPanelLateral(null);
  };

  /**
   * UNA grilla con la card de subir primera y, detrás, las creaciones del usuario — la
   * misma que "Variantes de acabado"
   * (ADR-2026-09-04-subir-imagen-es-la-primera-celda-de-la-grilla). Se dibuja igual en la
   * columna del paso 1 y en el panel lateral del paso 2.
   */
  const bloqueElegirImagen = (
    <GeneradasPickGrid
      companyId={companyId}
      selectedId={origen?.tipo === 'generada' ? (origen.id ?? null) : null}
      onSelect={elegirGenerada}
      rubro={rubro}
      emptyHint="Todavía no generaste ninguna imagen. Subí una desde tu computadora."
      tileInicial={
        <button
          type="button"
          onClick={() => inputImagenRef.current?.click()}
          className="aspect-square rounded-[13px] bg-brand-primary/[0.04] border-[1.5px] border-dashed border-brand-primary/35 hover:border-brand-primary/60 hover:bg-brand-primary/[0.07] grid place-items-center transition-colors"
        >
          <span className="flex flex-col items-center gap-1.5 px-2">
            <span className="w-9 h-9 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
              <ImageUp className="w-[18px] h-[18px]" />
            </span>
            <span className="text-[12.5px] font-semibold text-brand-primary text-center leading-tight">
              Subir imagen
            </span>
          </span>
        </button>
      }
    />
  );

  /**
   * Subir un extremo ("Cómo empieza" / "Cómo termina").
   *
   * Recibe también el valor ACTUAL del slot para poder soltar el blob del que reemplaza.
   * `esDeLaFoto` es el que NO se toca: esa url es la de la foto de partida, que sigue en
   * pantalla; soltarla dejaba la card de arriba con la imagen rota.
   */
  const onArchivoExtremo =
    (
      set: React.Dispatch<React.SetStateAction<ExtremoTransformacion | null>>,
      actual: ExtremoTransformacion | null,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const archivo = e.target.files?.[0];
      e.target.value = '';
      if (!archivo) return;
      const url = URL.createObjectURL(archivo);
      urlsCreadas.current.push(url);
      if (actual && !actual.esDeLaFoto) liberarUrl(actual.url);
      set({ url, nombre: archivo.name, esDeLaFoto: false, archivo });
      // Subir DESDE el panel lo cierra: la decisión ya se tomó y dejarlo abierto
      // mostrando creaciones que no se eligieron confunde.
      setPanelLateral(null);
    };

  // ── Panel lateral: dónde arranca ──────────────────────────────────────────
  // Se mide el borde derecho de la columna de configuración, como en Modelo y Catálogo
  // (ADR-2026-09-02-el-panel-lateral-arranca-donde-termina-la-configuracion): el ancho
  // es elástico y escribirlo acá lo dejaría tapando la columna en cuanto cambie.
  const areaRef = useRef<HTMLDivElement>(null);
  const panelConfigRef = useRef<HTMLElement>(null);
  const [offsetPanelLateral, setOffsetPanelLateral] = useState<number | string>(
    `calc(${ANCHO_PANEL_CONFIG} + ${SEPARACION_PANEL}px)`,
  );
  useLayoutEffect(() => {
    const aside = panelConfigRef.current;
    const area = areaRef.current;
    if (!aside || !area) return;
    const medir = () => {
      const a = aside.getBoundingClientRect();
      const r = area.getBoundingClientRect();
      setOffsetPanelLateral(Math.round(a.right - r.left) + SEPARACION_PANEL);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(aside);
    ro.observe(area);
    return () => ro.disconnect();
    // `paso` en las dependencias: cada paso dibuja su propio `<aside>`, y el observer
    // tiene que engancharse al que está montado.
  }, [paso]);

  const alternarPanel = (p: Exclude<PanelLateral, null>) =>
    setPanelLateral((actual) => (actual === p ? null : p));

  // ── Mobile: el back del chrome ────────────────────────────────────────────
  // El paso 2 NO dibuja su propio "Volver": el chrome flotante del shell ya pone uno
  // arriba a la izquierda, y las dos flechas harían el mismo movimiento una encima de la
  // otra (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2). Así que el back de
  // arriba retrocede DE PASO, y sólo desde el primero se va del modo. Además, ponerlo
  // esconde la barra inferior de navegación (`MobileBottomNav` se apaga con un override
  // puesto), que es lo que hacen el resto de los modos al ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      // Desde la configuración el back vuelve a la PRIMERA pantalla y no a la grilla: la
      // grilla es un paso de ida —se entra, se elige y se sale con "Siguiente"—, y volver
      // ahí devolvía a una pantalla de selección que ya estaba resuelta. Para cambiar la
      // foto está la card de "Imagen de partida", que abre esa misma grilla a propósito.
      // Los resultados sí vuelven a la configuración: es de donde salieron.
      if (paso === 'resultados') setPaso('configurar');
      else if (paso === 'configurar' || paso === 'imagen') setPaso('presentacion');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);

  // ── La tanda ──────────────────────────────────────────────────────────────
  const costo = costoVideo(duracion);
  const esTransforma = accion === 'transforma';
  /** "Dónde pasa" lo escribió el usuario en vez de elegir uno de los cinco curados. */
  const esAmbientePropio = ambienteId === AMBIENTE_PROPIO_ID;

  const falta: string | null = !origen
    ? 'Elegí una imagen.'
    : !esTransforma && esAmbientePropio && !ambientePropio.trim()
      ? 'Contá dónde pasa el video.'
      : esTransforma && !antes
      ? 'Subí la foto de cómo empieza.'
      : esTransforma && finalModo === 'descrito' && !finalDescrito.trim()
        ? 'Escribí cómo termina el mueble.'
        : esTransforma && finalModo === 'foto' && !despues
          ? 'Elegí la foto de cómo termina.'
          : null;

  const generar = () => {
    if (falta || !origen) return;
    const tanda: TandaVideo = {
      id: `${Date.now()}`,
      duracion,
      ratio: ratioDeFormato(formatoId),
      accion,
      creadaEn: Date.now(),
    };
    // TODO backend: acá va la llamada. Por ahora el payload queda a la vista para que
    // el que conecte el modo sepa exactamente qué viaja.
    console.info('[MostrarComoFuncionaMode] generar', {
      // De dónde sale la foto: una creación del usuario (por id) o un archivo suyo.
      origen:
        origen.tipo === 'generada'
          ? { tipo: 'generada', imagenId: origen.id }
          : { tipo: 'subida', archivo: origen.archivo?.name },
      accion,
      // Cómo termina: resuelto por la IA, descrito con palabras, o una foto.
      final: esTransforma
        ? finalModo === 'ia'
          ? { modo: 'ia' }
          : finalModo === 'descrito'
            ? { modo: 'descrito', texto: finalDescrito.trim() }
            : { modo: 'foto', archivo: despues?.archivo?.name ?? null }
        : null,
      ambiente: esTransforma
        ? null
        : esAmbientePropio
          ? { tipo: 'propio', texto: ambientePropio.trim() }
          : ambienteId,
      duracion,
      formato: formatoId,
      instruccion: instruccion.trim() || null,
      antes: esTransforma ? (antes?.archivo?.name ?? 'la foto de partida') : null,
      despues: esTransforma ? despues?.archivo?.name : null,
      creditos: costo,
    });
    setTandas((t) => [tanda, ...t]);
    // En el teléfono, generar TAPA la pantalla: overlay de "Generando el video…" y, al
    // terminar, la pantalla de resultados. La tanda ya está creada acá arriba —lo que
    // cambia es dónde se la mira—, así que cuando entre el backend el overlay pasa a
    // cerrarse con `completeNow` en vez de con el tiempo estimado.
    if (!esEscritorio) setGenerandoMobile(true);
  };

  // ── Migas ─────────────────────────────────────────────────────────────────
  // Igual que el Catálogo: el modo en el medio, el paso al final. Volver de "Configurar"
  // al producto es tocar el nombre del modo.
  const nombreModo = nombreCortoModo(PATH_MODO, 'Mostrar cómo funciona');
  const migas =
    paso === 'configurar'
      ? [
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo, onClick: () => setPaso('imagen') },
          { label: 'Configurar video' },
        ]
      : [{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: nombreModo }];

  // ── Piezas ────────────────────────────────────────────────────────────────

  const ambiente = ambientePorId(ambienteId as AmbienteId);
  const IconoAmbiente = esAmbientePropio ? PencilLine : ICONO_AMBIENTE[ambiente.id];

  const bloqueAccion = (
    <div className="shrink-0 rounded-2xl bg-black/[0.035] p-3 flex flex-col gap-2.5">
      <p className="px-1 text-[11px] font-bold uppercase tracking-wide text-black/45 leading-none">
        Qué pasa en el video
      </p>
      <div className="grid grid-cols-3 gap-2">
        {ACCIONES_VIDEO.map((a) => {
          const activa = a.id === accion;
          const Icono = ICONO_ACCION[a.id];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setAccion(a.id)}
              aria-pressed={activa}
              className={`flex flex-col items-center gap-2 px-2 py-3 rounded-xl bg-white transition-shadow ${
                activa
                  ? 'ring-2 ring-brand-primary'
                  : 'ring-1 ring-transparent hover:ring-black/[0.08]'
              }`}
            >
              <Icono
                className={`w-[26px] h-[26px] ${activa ? 'text-brand-primary' : 'text-black/55'}`}
              />
              <span
                className={`text-[12.5px] leading-tight text-center ${
                  activa ? 'font-semibold text-brand-black' : 'font-medium text-black/75'
                }`}
              >
                {a.nombre}
              </span>
            </button>
          );
        })}
      </div>
      <p className="px-1 text-[12.5px] leading-snug text-black/50">
        {accionVideoPorId(accion).descripcion}
      </p>
    </div>
  );

  // "Se transforma" pide DOS fotos: las otras acciones salen de una sola —el mueble no
  // cambia, cambia lo que pasa alrededor—. Sin el estado final la IA tiene que adivinar
  // en qué se convierte. Y como el ambiente sale de esas fotos, la card "Dónde pasa" no
  // se dibuja en este caso (ADR-2026-09-03-modo-mostrar-como-funciona).
  const bloqueAntesDespues = (
    <div className="shrink-0 rounded-2xl bg-black/[0.035] p-3 flex flex-col gap-2.5">
      <p className="px-1 text-[11px] font-bold uppercase tracking-wide text-black/45 leading-none">
        El antes y el después
      </p>
      <div className="flex items-center gap-2.5">
        <SlotExtremo
          etiqueta="Cómo empieza"
          extremo={antes}
          onElegir={() => inputAntesRef.current?.click()}
          onQuitar={() => {
            // Quitar una foto PROPIA suelta su blob; la que vino de la foto de partida
            // no es de este slot y sigue viva en `origen`.
            if (antes && !antes.esDeLaFoto) liberarUrl(antes.url);
            setAntes(null);
          }}
        />
        <span className="shrink-0 self-center mt-3 w-[26px] h-[26px] rounded-full bg-white grid place-items-center text-black/35 shadow-sm">
          <ArrowRight className="w-[15px] h-[15px]" />
        </span>
        <SlotExtremo
          etiqueta="Cómo termina"
          // Con `foto` el slot muestra la foto; con las otras dos maneras, el resumen de
          // lo decidido (ADR-2026-09-04-como-termina-tiene-tres-maneras).
          extremo={finalModo === 'foto' ? despues : null}
          resumen={
            finalModo === 'ia'
              ? { icono: Sparkles, titulo: 'Lo resuelve Atlas' }
              : finalModo === 'descrito' && finalDescrito.trim()
                ? { icono: PencilLine, titulo: finalDescrito.trim() }
                : null
          }
          textoVacio={
            finalModo === 'descrito' ? 'Escribí cómo termina' : 'Elegir cómo termina'
          }
          pie={
            finalModo === 'ia'
              ? 'Atlas interpreta el mueble'
              : finalModo === 'descrito'
                ? 'Con tus palabras'
                : 'Mismo mueble, mismo encuadre'
          }
          // Abre el panel y no el diálogo de archivos: ahí conviven las tres maneras.
          onElegir={() => alternarPanel('final')}
          onQuitar={() => {
            if (despues && !despues.esDeLaFoto) liberarUrl(despues.url);
            setDespues(null);
          }}
          abrePanelLateral
        />
      </div>
      {/* La bajada sigue a la manera elegida: prometer "dos fotos" cuando el final lo
          resuelve la IA o lo escribe el usuario era prometer de más. */}
      <p className="px-1 text-[12.5px] leading-snug text-black/50">
        {finalModo === 'foto'
          ? 'Dos fotos del mismo mueble, mismo encuadre. La IA arma el movimiento del medio; el ambiente y la luz salen de tus fotos.'
          : 'La IA arma el movimiento desde tu foto de partida; el ambiente y la luz salen de ella.'}
      </p>
      <input
        ref={inputAntesRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onArchivoExtremo(setAntes, antes)}
      />
      <input
        ref={inputDespuesRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onArchivoExtremo(setDespues, despues)}
      />
    </div>
  );

  /**
   * La foto de partida, con la misma forma de fila que el resto de las cards de la
   * columna. Reemplaza a `CardProductos`, que mostraba el producto del catálogo.
   *
   * En DESKTOP abre el panel lateral con la grilla; en MOBILE devuelve al paso 1, que es
   * donde esa grilla vive a pantalla completa — el mismo camino que "Editar la escena" en
   * "Ambiente completo". Tocarla no puede abrir una capa que muestre lo mismo que el paso
   * anterior.
   */
  const cardOrigen = (
    <CardFila
      etiqueta="Imagen de partida"
      valor={
        origen
          ? origen.tipo === 'generada'
            ? 'Una creación tuya'
            : origen.nombre
          : 'Sin elegir'
      }
      activa={panelLateral === 'origen'}
      onClick={() => (esEscritorio ? alternarPanel('origen') : setPaso('imagen'))}
      miniatura={
        origen ? (
          <CachedImg
            src={origen.url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <ImageUp className="w-6 h-6 text-black/35" strokeWidth={1.6} />
        )
      }
    />
  );

  const cardAmbiente = (
    <CardFila
      etiqueta="Dónde pasa"
      valor={
        esAmbientePropio
          ? ambientePropio.trim() || 'Escribí dónde pasa'
          : `${ambiente.nombre} · ${ambiente.descripcion}`
      }
      activa={panelLateral === 'ambiente'}
      onClick={() => alternarPanel('ambiente')}
      miniatura={
        !esAmbientePropio && ambiente.imageUrl ? (
          <CachedImg
            src={ambiente.imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <IconoAmbiente className="w-6 h-6 text-black/45" />
        )
      }
    />
  );

  /**
   * La duración, como CELDA del pie al lado de Formato
   * (ADR-2026-09-04-la-duracion-es-una-celda-del-pie).
   *
   * Es un ajuste de la salida, igual que el formato, y suelta arriba de la instrucción
   * —única barra a todo el ancho de la columna— pesaba más que las dos decisiones del
   * modo. Adentro de la celda la barra va compacta, con el valor al lado.
   */
  const celdaDuracion = (
    <CeldaAjuste etiqueta="Duración">
      <FillBarSliderV2
        label="Duración"
        variante="celda"
        min={DURACION_MIN}
        max={DURACION_MAX}
        step={1}
        value={duracion}
        onChange={setDuracion}
        formatValue={(v) => `${v} s`}
      />
    </CeldaAjuste>
  );

  /**
   * La instrucción libre, ÚLTIMA y a todo el alto que sobre.
   *
   * Es la pieza que le faltaba a la columna para leerse como la de Automático: allá el
   * espacio entre las decisiones y el pie lo llena la grilla de productos, que crece
   * (`flex-1`); acá el paso 2 no tiene grilla y el hueco quedaba en blanco.
   *
   * Va al final, como en Modelo y en Automático: primero se decide, y lo último es
   * afinar con palabras. El `flex-1` le da el sobrante y el `min-h` la sostiene cuando no
   * sobra nada (ADR-2026-09-03-el-paso-2-cierra-con-la-instruccion).
   */
  const bloqueInstruccion = (
    <div className="relative flex-1 min-h-[112px] flex flex-col">
      <textarea
        value={instruccion}
        onChange={(e) => setInstruccion(e.target.value)}
        placeholder="Agregá indicaciones extras para afinar el resultado"
        className="flex-1 w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
      />
      {/* El mismo historial de instrucciones que el resto de los modos
          (ADR-2026-08-12-historial-instrucciones-reusables). El `companyId` va explícito:
          esta pantalla vive afuera del `AgentEnvContext` de `InterfazV2`. */}
      <PromptRecientesButton
        tipo="instruccion"
        companyId={companyId}
        onPick={setInstruccion}
        titulo="Instrucciones usadas antes"
        className="absolute bottom-2.5 right-2.5"
      />
    </div>
  );

  const pastillaCreditos = (
    <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
      <img
        src="/images/token_icon.png"
        alt=""
        aria-hidden="true"
        className="w-3.5 h-3.5 object-contain"
      />
      {costo}
    </span>
  );

  // El pie fijo: en Video no van Calidad ni Fondo, igual que en el resto de los modos de
  // video (ver `AjustesRapidosProducto`) — queda Formato solo en su fila, y el Generar.
  const pie =
    paso === 'imagen' ? (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setPaso('configurar')}
          disabled={!origen}
          className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
        >
          Siguiente
        </button>
        {!origen && (
          <p className="text-[13px] text-black/50 text-center leading-snug">
            Elegí la foto del mueble que va a aparecer en el video.
          </p>
        )}
      </div>
    ) : (
      <>
        {/* Formato y Duración comparten fila: son los dos ajustes de la salida y se leen
            como un par, con la misma caja gris. */}
        <div className="grid grid-cols-2 gap-2">
          <CeldaAjuste
            etiqueta="Formato"
            onClick={() => alternarPanel('formato')}
            abrePanelLateral
          >
            <ValorConChevron texto={getFormatLabel(formatoId)} />
          </CeldaAjuste>
          {celdaDuracion}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={generar}
            disabled={!!falta}
            className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
          >
            Generar video
            {pastillaCreditos}
          </button>
          {/* El estado DEBAJO del botón, que no se mueve (ADR-2026-08-31 en Modelo). */}
          {falta && <p className="text-[13px] text-black/50 px-1">{falta}</p>}
        </div>
      </>
    );

  /**
   * El pie de MOBILE: el MISMO `pie` de la columna de desktop, anclado abajo.
   *
   * No se rehace con `BarraInferiorMobile` —la barra de Modelo— porque ésta lleva Calidad
   * y un contador de imágenes, y en video no va ninguna de las dos: los ajustes de acá
   * son Formato y Duración, que es justo lo que ya dibuja el pie de la columna. Reusarlo
   * es lo que hace que los dos pies digan lo mismo en las dos pantallas
   * (ADR-2026-09-03-modelo-mobile-toma-el-pie-de-desktop).
   */
  const pieMobile = (
    <div
      className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3 flex flex-col gap-2.5"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
    >
      {pie}
    </div>
  );

  // La cabecera de la pantalla —migas + título— ADENTRO de la columna, en su borde de
  // arriba, como en Automático (`cabeceraEnPanel` de `PresetDetailSheet`): así la fila de
  // columnas arranca pegada al techo y la columna se lee entera como la pantalla del modo.
  // Es la misma en los dos pasos; lo que cambia es el último nivel de las migas.
  const cabecera = (
    <div className="px-6 pt-5">
      <EncabezadoPantalla migas={migas} className="!px-0 !pt-0 !pb-2" />
      <h1
        key={paso}
        className="animate-paso-titulo text-[21px] font-semibold leading-tight text-brand-black"
      >
        {nombreModo}
      </h1>
    </div>
  );

  const claseColumna =
    'grow-0 shrink-0 flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden';

  // ── Paso 1: de dónde sale la foto ─────────────────────────────────────────
  // Las CREACIONES del usuario, no su catálogo de productos: el video parte de una foto
  // ya lograda, y el packshot crudo del catálogo es justo la que no se quiere poner en
  // movimiento (ADR-2026-09-04-mostrar-como-funciona-parte-de-una-creacion). La card de
  // subir es la primera celda de la misma grilla.
  const columnaImagen = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      {cabecera}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
        <div className="flex-1 min-h-0 overflow-y-auto pb-4">
          <div className="mb-4">
            <p className="text-[17px] font-bold text-brand-black leading-tight">
              Elegí la imagen
            </p>
            <p className="text-[13.5px] text-black/55 leading-relaxed mt-1">
              La foto del mueble que va a aparecer en el video. Una sola.
            </p>
          </div>
          {bloqueElegirImagen}
        </div>
        <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </aside>
  );

  // ── Paso 2: la configuración del video ────────────────────────────────────
  const columnaConfiguracion = (
    <aside
      ref={panelConfigRef}
      style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
      className={claseColumna}
    >
      {cabecera}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pb-4">
          {cardOrigen}
          {paso === 'configurar' && (
            <>
              {bloqueAccion}
              {esTransforma ? bloqueAntesDespues : cardAmbiente}
              {bloqueInstruccion}
            </>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </aside>
  );

  /**
   * Las tandas generadas. Sale de `columnaDerecha` porque en el teléfono no hay columna
   * de la derecha: van PRIMERAS en el scroll del paso 2, que es donde se las va a buscar
   * apenas se toca Generar (el mismo lugar que en "Ambiente completo").
   *
   * El `max-w-[420px]` de cada recuadro es un tope, no un ancho: en un teléfono el video
   * ocupa lo que hay.
   */
  const listaTandas = tandas.map((t) => (
    <section key={t.id} className="flex flex-col gap-2.5">
      <h4 className="text-[13px] font-semibold leading-none text-black/75">
        1 video · {t.duracion} s
        <span className="mx-[7px] inline-block h-[3px] w-[3px] rounded-full bg-[#3C3C43]/35 align-middle" />
        <span className="font-medium text-[#3C3C43]/50">
          {accionVideoPorId(t.accion).nombre}
        </span>
      </h4>
      <div className="max-w-[420px]">
        <RecuadroGenerando ratio={t.ratio} duracion={t.duracion} />
      </div>
    </section>
  ));

  const columnaDerecha = (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {tandas.length === 0 ? (
        // La MUESTRA del modo hasta que haya algo generado
        // (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): un lienzo en blanco dejaba media
        // pantalla sin decir nada justo cuando falta saber qué hace el modo.
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6">
          <div className="shrink-0 text-center max-w-[440px] mb-4">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              {TITULO_MUESTRA}
            </h2>
            <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
              {BAJADA_MUESTRA}
            </p>
          </div>
          {/* La imagen es HIJA DIRECTA de la columna, sin caja `flex-1` en el medio: esa
              caja se llevaba todo el alto sobrante y centraba la foto adentro, así que
              entre el título y la imagen aparecían ~90px de aire que no los ponía ningún
              margen. Así, el `justify-center` de arriba centra al par —título + foto— como
              un bloque, y lo que los separa es sólo el `mb-4`
              (ADR-2026-09-03-la-muestra-del-modo-es-un-bloque). `min-h-0` es lo que deja
              que la foto se achique cuando la ventana es baja. */}
          <img
            src={PORTADA}
            alt="Ejemplo del modo Mostrar cómo funciona"
            className="block min-h-0 w-auto max-w-[420px] object-contain rounded-2xl"
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 pb-2">
          {listaTandas}
        </div>
      )}
    </div>
  );

  /** El mismo bloque —subir o elegir una creación— que la columna del paso 1. */
  const panelOrigen = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      <p className="text-[12.5px] text-black/60 mb-4">
        La foto del mueble que va a aparecer en el video. Una sola: elegirla cierra el
        panel.
      </p>
      {bloqueElegirImagen}
    </div>
  );

  const panelAmbiente = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      <p className="text-[12.5px] text-[#3C3C43]/60 mb-4">
        El lugar donde pasa el video. La luz y lo que hay alrededor salen de acá.
      </p>
      <div
        className="grid gap-3"
        // Dos columnas en el teléfono: a tres, el nombre y la bajada de cada ambiente
        // ("Lo describo yo", "Exterior, sol pleno y verde de fondo") entran de a dos
        // palabras por renglón y la card se vuelve una columna de texto partido.
        style={{
          gridTemplateColumns: `repeat(${
            esEscritorio ? COLUMNAS_PANEL_LATERAL : 2
          }, minmax(0, 1fr))`,
        }}
      >
        {AMBIENTES.map((a) => {
          const activa = a.id === ambienteId;
          const Icono = ICONO_AMBIENTE[a.id];
          return (
            <button
              key={a.id}
              type="button"
              aria-pressed={activa}
              onClick={() => {
                setAmbienteId(a.id);
                setPanelLateral(null);
              }}
              className={`relative flex flex-col text-left rounded-2xl overflow-hidden bg-neutral-50 transition-shadow ${
                activa ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06] hover:ring-black/[0.14]'
              }`}
            >
              <div className="relative aspect-[4/3] bg-neutral-100 grid place-items-center">
                {a.imageUrl ? (
                  <CachedImg
                    src={a.imageUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <Icono className="w-8 h-8 text-black/35" strokeWidth={1.5} />
                )}
                {activa && (
                  <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                  </span>
                )}
              </div>
              <div className="px-3 py-2.5">
                <p className="text-[13.5px] font-semibold text-brand-black leading-tight">
                  {a.nombre}
                </p>
                <p className="text-[12px] text-black/50 leading-snug mt-0.5">
                  {a.descripcion}
                </p>
              </div>
            </button>
          );
        })}

        {/* La sexta: describirlo a mano. Misma tarjeta que las curadas —para que se lea
            como una opción más de la misma decisión y no como un escape—, pero punteada y
            en azul, que es como la app viene marcando "esto lo ponés vos"
            (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).

            NO cierra el panel al elegirla: elegir los cinco curados termina la decisión,
            pero acá recién empieza —hay que escribir—, y cerrar dejaría al usuario mirando
            una card que dice "Escribí dónde pasa" sin dónde hacerlo. */}
        <button
          type="button"
          aria-pressed={esAmbientePropio}
          onClick={() => setAmbienteId(AMBIENTE_PROPIO_ID)}
          className={`relative flex flex-col text-left rounded-2xl overflow-hidden transition-shadow ${
            esAmbientePropio
              ? 'bg-brand-primary/[0.04] ring-2 ring-brand-primary'
              : 'bg-brand-primary/[0.02] ring-[1.5px] ring-dashed ring-brand-primary/35 hover:ring-brand-primary/60'
          }`}
        >
          <div className="relative aspect-[4/3] grid place-items-center">
            <span className="w-10 h-10 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
              <PencilLine className="w-[18px] h-[18px]" />
            </span>
            {esAmbientePropio && (
              <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m5 12 5 5L20 7" />
                </svg>
              </span>
            )}
          </div>
          <div className="px-3 py-2.5">
            <p className="text-[13.5px] font-semibold text-brand-primary leading-tight">
              {AMBIENTE_PROPIO.nombre}
            </p>
            <p className="text-[12px] text-black/50 leading-snug mt-0.5">
              {AMBIENTE_PROPIO.descripcion}
            </p>
          </div>
        </button>
      </div>

      {/* El campo aparece DEBAJO de la grilla y sólo con la opción elegida: el panel es la
          decisión entera, así que escribir tiene que pasar acá adentro y no en otra
          pantalla. Es el mismo textarea que la instrucción de la columna, con su historial
          de instrucciones usadas antes. */}
      {esAmbientePropio && (
        <div className="mt-4">
          <label
            htmlFor="ambiente-propio"
            className="block text-[12.5px] font-semibold text-brand-black mb-1.5"
          >
            Contá dónde pasa
          </label>
          <div className="relative">
            <textarea
              id="ambiente-propio"
              autoFocus
              value={ambientePropio}
              onChange={(e) => setAmbientePropio(e.target.value)}
              placeholder="Un loft con piso de cemento y ventanal al norte, luz de media tarde"
              className="w-full min-h-[104px] rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
            />
            <PromptRecientesButton
              tipo="instruccion"
              companyId={companyId}
              onPick={setAmbientePropio}
              titulo="Instrucciones usadas antes"
              className="absolute bottom-2.5 right-2.5"
            />
          </div>
          <p className="mt-2 text-[12px] leading-snug text-black/45">
            El mueble y su forma salen de tu foto: acá va lo de alrededor —el lugar, los
            objetos y la luz—.
          </p>
        </div>
      )}
    </div>
  );

  /**
   * "Cómo termina": las creaciones del MISMO producto que la foto de partida, más subir
   * una nueva (ADR-2026-09-04-como-termina-se-elige-de-las-creaciones-del-producto).
   *
   * La foto del mueble ABIERTO o desplegado suele ser una foto propia que todavía no se
   * generó, así que subir sigue estando —primera celda de la grilla, como en el paso 1—;
   * pero si esa vertiente ya se generó, está a un toque y sin buscarla entre todo.
   */
  const panelFinal = (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Las TRES maneras, arriba y a la vista: la IA sola, con palabras, o con una foto
          (ADR-2026-09-04-como-termina-tiene-tres-maneras). El mismo dibujo que el bloque
          de fondo de "Variantes de acabado": caja gris, título, bajada y una card por
          opción con su descripción adentro. */}
      <div className="shrink-0 px-6">
        <div className="rounded-2xl bg-black/[0.028] p-3.5">
          <p className="text-[12.5px] leading-snug text-black/55">
            En qué termina el mueble: abierto, extendido, desplegado. Es el otro extremo
            del video.
          </p>
          {/* Una sola columna en el teléfono: son tres cards con título Y bajada, y a
              tres columnas de 110px cada bajada caía en seis renglones. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
            {OPCIONES_FINAL.map((o) => {
              const activo = o.id === finalModo;
              const Icono = ICONO_FINAL[o.id];
              return (
                <button
                  key={o.id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => setFinalModo(o.id)}
                  className={`rounded-xl bg-white px-3 py-2.5 text-left transition-shadow ${
                    activo
                      ? 'ring-2 ring-inset ring-brand-primary'
                      : 'ring-1 ring-inset ring-black/[0.08] hover:ring-black/20'
                  }`}
                >
                  <span className="flex items-start gap-1.5">
                    <Icono
                      className={`w-4 h-4 shrink-0 mt-[1px] ${
                        activo ? 'text-brand-primary' : 'text-black/45'
                      }`}
                      strokeWidth={1.7}
                    />
                    <span className="min-w-0 text-[13px] font-semibold leading-tight text-brand-black">
                      {o.nombre}
                    </span>
                  </span>
                  <span className="block text-[11.5px] leading-snug text-black/50 mt-1">
                    {o.descripcion}
                  </span>
                </button>
              );
            })}
          </div>
          {finalModo === 'descrito' && (
            <div className="mt-2.5">
              <input
                value={finalDescrito}
                onChange={(e) => setFinalDescrito(e.target.value)}
                autoFocus
                placeholder="El sofá cama abierto, con el colchón a la vista…"
                className="w-full h-10 px-3.5 rounded-xl bg-white ring-1 ring-inset ring-black/[0.08] focus:ring-brand-primary/45 focus:outline-none text-[13px] text-brand-black placeholder:text-black/35 transition-shadow"
              />
            </div>
          )}
        </div>
      </div>

      {/* La grilla sólo con "Uso una foto": con las otras dos no hay nada que elegir, y
          dejarla abajo invitaba a tocarla sin cambiar de manera. */}
      {finalModo === 'foto' ? (
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
          <p className="text-[12.5px] leading-snug text-[#3C3C43]/60 mb-3">
            {origen?.producto
              ? `El mismo mueble, en su otro estado. Estás viendo las creaciones de ${origen.producto}.`
              : 'El mismo mueble, en su otro estado.'}
          </p>
          <GeneradasPickGrid
            companyId={companyId}
            selectedId={null}
            onSelect={elegirFinalGenerada}
            productoFijo={origen?.varianteId ?? null}
            rubro={rubro}
            emptyHint={
              origen?.producto
                ? `Todavía no hay otras creaciones de ${origen.producto}. Subí la foto de cómo termina.`
                : 'Todavía no generaste ninguna imagen. Subí la foto de cómo termina.'
            }
            tileInicial={
              <button
                type="button"
                onClick={() => inputDespuesRef.current?.click()}
                className="aspect-square rounded-[13px] bg-brand-primary/[0.04] border-[1.5px] border-dashed border-brand-primary/35 hover:border-brand-primary/60 hover:bg-brand-primary/[0.07] grid place-items-center transition-colors"
              >
                <span className="flex flex-col items-center gap-1.5 px-2">
                  <span className="w-9 h-9 rounded-full bg-brand-primary/10 grid place-items-center text-brand-primary">
                    <Upload className="w-[18px] h-[18px]" />
                  </span>
                  <span className="text-[12.5px] font-semibold text-brand-primary text-center leading-tight">
                    Subir foto
                  </span>
                </span>
              </button>
            }
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0" />
      )}

      {/* Pie con "Listo": con la IA y con el texto no hay nada que tocar en la grilla, así
          que hace falta un lugar donde cerrar el panel. */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-3.5 border-t border-black/[0.06]">
        <p className="text-[13px] text-black/50">
          {finalModo === 'ia'
            ? 'Atlas resuelve el final'
            : finalModo === 'descrito'
              ? finalDescrito.trim()
                ? 'Listo para generar'
                : 'Escribí cómo termina'
              : despues
                ? 'Foto elegida'
                : 'Elegí o subí la foto'}
        </p>
        <button
          type="button"
          onClick={() => setPanelLateral(null)}
          className="h-10 px-5 rounded-2xl bg-brand-primary text-white text-[14px] font-semibold hover:opacity-90 transition-opacity"
        >
          Listo
        </button>
      </div>
    </div>
  );

  const panelFormato = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-3">
      <FormatPickerGrid
        selectedId={formatoId}
        onPick={(id) => {
          setFormatoId(id);
          setPanelLateral(null);
        }}
        columnas={COLUMNAS_PANEL_LATERAL}
      />
    </div>
  );

  const tituloPanel =
    panelLateral === 'origen'
      ? 'Imagen de partida'
      : panelLateral === 'ambiente'
        ? 'Dónde pasa'
        : panelLateral === 'final'
          ? 'Cómo termina'
          : 'Formato';

  // ── MOBILE: las dos pantallas ─────────────────────────────────────────────
  // Full-screen debajo del chrome flotante del shell (el back y el menú viven arriba, en
  // 3.5rem de alto más el safe area). Es UN contenedor y no dos portales: acá el modo es
  // la pantalla —no un sheet montado sobre otra—, así que no hay nada debajo que tapar.
  /**
   * La foto elegida, con la forma de producto que espera `CardProductos`.
   *
   * No es un producto del catálogo —este modo parte de una creación— pero la card sólo
   * necesita id, nombre y foto, y así la pantalla de arranque muestra lo elegido en vez
   * de su estado vacío. Vacío mientras no haya nada: ahí la card cae en las miniaturas del
   * catálogo, que son la muestra de lo que hay del otro lado.
   */
  const productoDeLaCard = origen
    ? [
        {
          id: origen.key,
          name:
            origen.tipo === 'generada'
              ? (origen.producto ?? 'Una creación tuya')
              : origen.nombre,
          imageUrl: origen.url,
        },
      ]
    : [];

  const pantallaMobile = (
    <div
      className="md:hidden fixed inset-0 z-30 bg-white flex flex-col"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {paso === 'presentacion' ? (
        // ── Pantalla 1: la presentación ─────────────────────────────────────
        // La MUESTRA del modo —qué hace, con un ejemplo— y abajo la card para traer la
        // foto, con la misma anatomía que "Variantes de acabado" y "Ambiente completo"
        // (ADR-2026-09-10-ambiente-completo-abre-con-la-muestra). Antes la muestra viajaba
        // arriba de la grilla, adentro del mismo scroll: la explicación del modo compartía
        // pantalla con una grilla que ya pedía elegir.
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 pb-2">
            <div className="shrink-0 text-center max-w-[340px] mb-6">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                {TITULO_MUESTRA}
              </h2>
              <p className="text-[14px] text-[#3C3C43]/60 leading-snug mt-1">
                {BAJADA_MUESTRA}
              </p>
            </div>
            {/* El tope es el ALTO y no el ancho: esta portada es VERTICAL (928×1152), así
                que con `max-w` se iba a los bordes de la pantalla y bajaba 500px. Con el
                alto tomado, el ancho sale de la proporción y queda del tamaño de las
                portadas de los otros modos, que son casi cuadradas. */}
            <img
              src={PORTADA}
              alt="Ejemplo del modo Mostrar cómo funciona"
              className="block min-h-0 max-h-[300px] w-auto max-w-full object-contain rounded-2xl"
            />
          </div>
          {/* UN SOLO control, la CARD de Modelo (`CardProductos`): la fila gris con el
              abanico de miniaturas y el chevron de "esto abre algo". Vacía muestra las
              últimas fotos del catálogo, como muestra de que hay algo del otro lado; con
              la foto ya elegida muestra ESA, así que volver acá desde la configuración no
              parece haber perdido lo elegido.

              Sin el "Subir nuevo producto" que Modelo pone al lado: la pantalla siguiente
              ya abre con la celda de subir como primera de la grilla, así que un segundo
              camino acá arriba ofrecería dos veces la misma cosa. */}
          <div className="shrink-0 px-5 pt-4 pb-5 flex flex-col gap-2.5">
            <CardProductos
              products={productoDeLaCard}
              copyElegido="Cambiar la imagen"
              placeholders={
                miniaturasCatalogo.length > 0
                  ? miniaturasCatalogo
                  : PLACEHOLDERS_MUEBLES
              }
              onAbrirProductos={() => setPaso('imagen')}
              onSubirArchivos={() => setPaso('imagen')}
            />
            {/* El "Siguiente" aparece SÓLO con la foto elegida, que es cuando esta
                pantalla deja de ser una presentación y pasa a ser el lugar al que se
                vuelve: sin él, volver de la configuración obligaba a entrar a la grilla y
                salir de nuevo para seguir. */}
            {origen && (
              <button
                type="button"
                onClick={() => setPaso('configurar')}
                className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                Siguiente
              </button>
            )}
          </div>
        </div>
      ) : paso === 'imagen' ? (
        <>
          {/* El título va ADENTRO del scroll y no clavado arriba: en un teléfono, fijo se
              come la mitad de la grilla, que es lo único que hay que hacer en este paso. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">
            <div className="pt-1 pb-4">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                Elegí la imagen
              </h2>
              <p className="text-[13.5px] text-black/55 leading-relaxed mt-1">
                La foto del mueble que va a aparecer en el video. Una sola.
              </p>
            </div>
            {bloqueElegirImagen}
          </div>
          {pieMobile}
        </>
      ) : paso === 'resultados' ? (
        // ── Pantalla 4: lo que salió ────────────────────────────────────────
        // El video en una pantalla PROPIA, detrás del overlay de generación. Antes la
        // tanda se dibujaba arriba de las cards de la configuración: la pantalla era mitad
        // resultado y mitad formulario, y lo recién generado se veía por la mitad. En
        // desktop no hace falta —la tanda cae en la columna de la derecha, al lado y no
        // encima—; en el teléfono no hay costado.
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Tu video
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-6">
            {listaTandas}
          </div>
          {/* Volver a la configuración con todo lo elegido intacto: se llega igual con el
              back del chrome, pero el camino de ida —cambiar algo y generar de nuevo— es
              el que merece un botón. */}
          <div
            className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
          >
            <button
              type="button"
              onClick={() => setPaso('configurar')}
              className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              Seguir generando
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col page-slide-in-right">
          {/* SIN "Volver" propio: el chrome flotante dibuja el suyo justo encima de esta
              fila y vuelve a la primera pantalla —ver el effect de `mobileBackOverride`—. */}
          <div className="shrink-0 px-5 pb-3">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              Configurar el video
            </h2>
          </div>
          {/* SIN la tanda arriba: lo generado vive en su propia pantalla
              (`resultados`). Acá quedan sólo los ajustes. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
            {cardOrigen}
            {bloqueAccion}
            {esTransforma ? bloqueAntesDespues : cardAmbiente}
            {bloqueInstruccion}
          </div>
          {pieMobile}
        </div>
      )}
    </div>
  );

  // Lo que en desktop se elige en el panel lateral, en el teléfono se elige en una capa
  // full-screen con EL MISMO contenido (`CapaModoMobile`). "Imagen de partida" no está:
  // en mobile esa card devuelve al paso 1, que es esa misma grilla a pantalla completa.
  const capasMobile = (
    <>
      <CapaModoMobile
        abierta={panelLateral === 'ambiente'}
        onCerrar={() => setPanelLateral(null)}
        titulo="Dónde pasa"
      >
        {panelAmbiente}
      </CapaModoMobile>
      <CapaModoMobile
        abierta={panelLateral === 'final'}
        onCerrar={() => setPanelLateral(null)}
        titulo="Cómo termina"
      >
        {panelFinal}
      </CapaModoMobile>
      {/* Formato sí es un bottom sheet: es el mismo de toda la app y es una lista de
          opciones que commitea con el toque. */}
      <FormatPicker
        open={panelLateral === 'formato'}
        selectedId={formatoId}
        onChange={setFormatoId}
        onClose={() => setPanelLateral(null)}
      />
    </>
  );

  return (
    <>
          {!esEscritorio && (
            <>
              {pantallaMobile}
              {capasMobile}
              {/* El overlay de generación, el MISMO de Modelo y de Carrusel
                  (`LoadingAnimation`): tapa la pantalla entera mientras se genera y deja
                  al usuario en `resultados`. En desktop no se monta — allá la tanda
                  aparece en la columna de la derecha y tapar la pantalla escondería justo
                  lo que hay que mirar. */}
              {generandoMobile && (
                <LoadingAnimation
                  message="Generando el video…"
                  subMessage="Esto puede tardar unos minutos"
                  durationMs={CARGA_MOCK_MS}
                  completeAtEnd
                  onCompleted={() => {
                    setGenerandoMobile(false);
                    setPaso('resultados');
                  }}
                />
              )}
            </>
          )}

          {/* ── DESKTOP: el marco de las pantallas de modo ─────────────────── */}
          {/* La MISMA geometría que los modos-sección (Automático): a ras del shell
              —`inset-y-0 right-0`, sin el marco flotante ni el borde—, sobre el gris de
              la app y redondeado sólo del lado de la sidebar. Antes iba con
              `bg-surface-content` + `BORDE_PANTALLA` + `MARCO_PANTALLA`, o sea una TARJETA
              BLANCA que envolvía a las dos columnas: dos cajas claras, una adentro de la
              otra (ADR-2026-09-03-el-modo-no-lleva-tarjeta-alrededor-de-las-columnas).

              `lg:pt-[52px]` es el aire de arriba de las pantallas del shell — el mismo que
              deja Automático, y lo que alinea el techo de la columna con el suyo. */}
          {esEscritorio && (
          <div
            className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-mobile rounded-tl-[26px] rounded-bl-[26px] flex-col lg:pt-[52px]"
            style={{ left: sidebarWidth }}
          >
            <div
              ref={areaRef}
              // `flex-row-reverse` + `justify-end`: la configuración a la IZQUIERDA y el
              // DOM con la presentación primero, como en los otros modos
              // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660). Sin
              // encabezado afuera: las migas y el título van adentro de la columna.
              //
              // `FILA_MODO_REDISENO`: 52px de sangría a cada lado, el mismo aire que hay
              // arriba, en vez del 90% centrado que dejaba ~104px de un lado y la columna
              // sin llegar al borde (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde).
              className={`relative flex-1 min-h-0 px-8 pb-6 flex flex-row-reverse justify-end gap-6 items-stretch ${FILA_MODO_REDISENO}`}
            >
              {columnaDerecha}
              {paso === 'imagen' ? columnaImagen : columnaConfiguracion}

              <PanelLateralModo
                abierto={panelLateral !== null}
                onCerrar={() => setPanelLateral(null)}
                titulo={tituloPanel}
                maxAncho={ANCHO_PANEL_LATERAL}
                offsetIzquierdo={offsetPanelLateral}
                contenedorRef={areaRef}
              >
                {panelLateral === 'origen'
                  ? panelOrigen
                  : panelLateral === 'ambiente'
                    ? panelAmbiente
                    : panelLateral === 'final'
                      ? panelFinal
                      : panelFormato}
              </PanelLateralModo>
            </div>
          </div>
          )}

          {/* El input de la foto de partida vive ACÁ y no adentro del panel: el panel se
              desmonta al cerrarse, y el diálogo de archivos del navegador no cuenta como
              "click adentro", así que elegir el archivo lo cerraba y el `onChange` caía
              en un input que ya no existía. */}
          <input
            ref={inputImagenRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onArchivoImagen}
          />
    </>
  );
}
